import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import Input from 'tailwind-components/Input/Input';
import Button from 'tailwind-components/Button/Button';
import BrandLogo from 'tailwind-components/BrandLogo/BrandLogo';
import { LogOut } from 'state/actions/actions';
import { getDefaultAuthenticatedPath } from 'helpers/defaultAuthenticatedPath';
import useForceChangePassword from './useForceChangePassword';

/**
 * blocking screen shown to users created with a temporary password;
 * they must set a new password before using the portal
 */
export default function ForceChangePassword() {
  const dispatch = useDispatch();
  const { zohoId, zohoData: user } = useSelector(state => state.user);
  const { authenticated, mustChangePassword } = useSelector(
    state => state.authentication
  );
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const { loading, errors, submit } = useForceChangePassword();

  if (!authenticated || !zohoId) {
    return <Navigate to='/login' replace />;
  }
  // also acts as the post-success redirect once the claim is cleared
  if (!mustChangePassword) {
    return <Navigate to={getDefaultAuthenticatedPath(user)} replace />;
  }

  // merged so hook errors and local validation errors render the same way
  const allErrors = { ...formErrors, ...errors };

  function validate() {
    const validationErrors = {};
    if (!currentPassword) {
      validationErrors.currentPassword = 'This field is required';
    }
    if (!newPassword) {
      validationErrors.newPassword = 'This field is required';
    }
    if (newPassword && newPassword === currentPassword) {
      validationErrors.newPassword =
        'Your new password must be different from the temporary one';
    }
    if (newPassword !== confirmNewPassword) {
      validationErrors.confirmNewPassword = 'Passwords do not match';
    }
    return validationErrors;
  }

  function handleSubmit(event) {
    event.preventDefault();
    const validationErrors = validate();
    setFormErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    submit(currentPassword, newPassword);
  }

  return (
    <div className='flex min-h-screen items-center justify-center bg-gray-50 px-4'>
      <div className='w-full max-w-md rounded-lg bg-white p-8 shadow-md'>
        <BrandLogo
          alt='Company logo'
          className='mx-auto mb-6 h-16'
          agencyClassName='max-w-[240px]'
        />
        <h1 className='mb-2 text-center text-xl font-semibold'>
          Set your new password
        </h1>
        <p className='mb-6 text-center text-sm text-gray-600'>
          For your security, you must replace your temporary password before
          using the portal.
        </p>
        <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
          <Input
            name='currentPassword'
            label='Temporary password (or your current password)'
            type='password'
            error={allErrors.currentPassword}
            placeholder='Enter your temporary password'
            onChange={e => setCurrentPassword(e.target.value)}
            value={currentPassword}
          />
          <Input
            name='newPassword'
            label='New password'
            type='password'
            error={allErrors.newPassword}
            placeholder='Enter your new password'
            onChange={e => setNewPassword(e.target.value)}
            value={newPassword}
          />
          <Input
            name='confirmNewPassword'
            label='Confirm new password'
            type='password'
            error={allErrors.confirmNewPassword}
            placeholder='Confirm your new password'
            onChange={e => setConfirmNewPassword(e.target.value)}
            value={confirmNewPassword}
          />
          {allErrors.general && (
            <p className='text-sm text-red-600'>{allErrors.general}</p>
          )}
          <Button
            text='Set new password'
            fill
            variant='primaryLight'
            isLoading={loading}
            type='submit'
            className='mt-2'
          />
        </form>
        <Button
          type='button'
          variant='danger'
          onClick={() => dispatch(LogOut())}
          className='mt-4'
        >
          Log out
        </Button>
      </div>
    </div>
  );
}
